import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { PageHeader } from "@/components/PageHeader";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  archiveExpense, deleteExpense, listCategories, listExpenses, listOccurrences,
  listPayments,
  listVendors, unarchiveExpense,
} from "@/lib/api";
import { EUR, fmtDate, paymentMethodIT, statusIT } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import {
  Search, X, Plus, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti gli stati" },
  { value: "planned", label: "In programma" },
  { value: "partially_paid", label: "Parziale" },
  { value: "paid", label: "Pagata" },
  { value: "overdue", label: "Scaduta" },
  { value: "cancelled", label: "Annullata" },
  { value: "skipped", label: "Saltata" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * "Spese ordinarie" — single-occurrence operational expenses.
 *
 * Each ordinary expense has exactly one occurrence (single due date / amount).
 * The list is therefore built by joining each ordinary expense to its sole
 * occurrence so the table can show:
 *   - status (planned / paid / overdue / etc.)
 *   - paid amount + remaining
 *   - payment method (the LAST registered payment if any)
 *
 * Filters: month, year, free-text date range, vendor, category, status, method.
 */
export default function OrdinaryExpenses() {
  const today = new Date();
  const [rows, setRows] = useState([]); // {expense, occurrence, lastMethod}
  const [cats, setCats] = useState([]);
  const [vens, setVens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Filters
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState(`${today.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${today.getFullYear()}-12-31`);
  const [vendorId, setVendorId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("");

  // Sort & pagination
  const [sort, setSort] = useState({ key: "due_date", direction: "desc" });
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [pageSize, setPageSize] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      // Single batched fetch: one call for expenses, one for occurrences
      // scoped to ordinary, plus categories/vendors in parallel. Replaces the
      // previous N+1 fan-out (one /occurrences call per expense).
      const [exps, occs, c, v, pays] = await Promise.all([
        listExpenses({ is_ordinary: true, include_archived: showArchived }),
        listOccurrences({ is_ordinary: true, limit: 1000, include_archived: showArchived }),
        listCategories(),
        listVendors(),
        // Iter43: batch fetch dei payments per esporre il metodo di
        // pagamento nella riga (usato dal PDF export).
        listPayments({ limit: 5000 }),
      ]);
      setCats(c || []);
      setVens(v || []);
      const occByExp = new Map((occs || []).map((o) => [o.expense_id, o]));
      // Mappa occurrence_id → metodo del pagamento più recente. Le ordinarie
      // "Già pagata" hanno tipicamente 1 solo pagamento, ma se ce ne sono più
      // (registrazioni multiple) vince il più recente.
      const methodByOcc = new Map();
      for (const p of (pays || [])) {
        const prev = methodByOcc.get(p.occurrence_id);
        if (!prev || (p.paid_at || "") > (prev.date || "")) {
          methodByOcc.set(p.occurrence_id, { method: p.method, date: p.paid_at });
        }
      }
      const decorated = (exps || []).map((e) => {
        const occ = occByExp.get(e.id) || null;
        return {
          expense: e,
          occurrence: occ,
          payment_method: occ ? (methodByOcc.get(occ.id)?.method || null) : null,
        };
      });
      setRows(decorated);
    } catch {
      toast.error("Impossibile caricare le spese ordinarie.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const venMap = useMemo(() => Object.fromEntries(vens.map((v) => [v.id, v])), [vens]);

  // Debounce del campo cerca (300ms): l'utente digita nell'Input
  // controllato, ma il `filtered` useMemo (con `sortedFiltered` che
  // dipende da tutto) scatta solo dopo l'ultima keystroke stabile.
  const debouncedQuery = useDebouncedValue(query, 300);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return rows.filter(({ expense, occurrence }) => {
      const due = occurrence?.due_date || expense.start_date;
      if (due && (due < dateFrom || due > dateTo)) return false;
      if (vendorId && expense.vendor_id !== vendorId) return false;
      if (categoryId && expense.category_id !== categoryId) return false;
      if (statusFilter !== "all" && occurrence?.status !== statusFilter) return false;
      if (q) {
        const hay = [
          expense.title,
          expense.description,
          expense.notes,
          venMap[expense.vendor_id]?.name,
          catMap[expense.category_id]?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, debouncedQuery, dateFrom, dateTo, vendorId, categoryId, statusFilter, catMap, venMap]);

  // Sorting — memoized. Default: due_date desc (most recent first).
  const sortedFiltered = useMemo(() => {
    const { key, direction } = sort;
    const dir = direction === "asc" ? 1 : -1;
    const todayIso = new Date().toISOString().slice(0, 10);
    const getter = ({ expense, occurrence }) => {
      switch (key) {
        case "due_date":
          return occurrence?.due_date || expense.start_date || "";
        case "vendor":
          return (venMap[expense.vendor_id]?.name || "").toLowerCase();
        case "description":
          return (
            (occurrence?.effective_title || expense.title || "") +
            " " + (expense.description || "")
          ).toLowerCase();
        case "category":
          return (catMap[expense.category_id]?.name || "").toLowerCase();
        case "amount":
          return Number(occurrence?.effective_amount ?? expense.planned_amount ?? 0);
        case "status": {
          // Compute the effective status used in the table so the sort order
          // matches what the user sees ("Scaduta" included).
          const s = occurrence?.status || "planned";
          const due = occurrence?.due_date || expense.start_date;
          if ((s === "planned" || s === "partially_paid") && due && due < todayIso) {
            return "overdue";
          }
          return s;
        }
        default:
          return "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [filtered, sort, venMap, catMap]);

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  // Keep the page in-bounds when filters/sort change the result set.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
  const paged = useMemo(
    () =>
      sortedFiltered.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [sortedFiltered, page, pageSize],
  );

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) {
        // First click: pick a sensible direction per column type.
        const initialDesc =
          key === "due_date" || key === "amount" ? "desc" : "asc";
        return { key, direction: initialDesc };
      }
      return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
    setPage(1);
  };

  // Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
  }, [query, dateFrom, dateTo, vendorId, categoryId, statusFilter]);

  // KPIs
  const kpi = useMemo(() => {
    let planned = 0, paid = 0, count = filtered.length, paidCount = 0, overdue = 0;
    const todayIso = new Date().toISOString().slice(0, 10);
    for (const { occurrence } of filtered) {
      if (!occurrence) continue;
      const eff = Number(occurrence.effective_amount || 0);
      const pd = Number(occurrence.total_paid || 0);
      planned += eff;
      paid += pd;
      if (occurrence.status === "paid") paidCount++;
      if (
        (occurrence.status === "planned" || occurrence.status === "partially_paid") &&
        occurrence.due_date < todayIso
      ) overdue++;
    }
    return {
      planned, paid, remaining: planned - paid, count, paidCount, overdue,
    };
  }, [filtered]);

  const exportRows = filtered.map(({ expense, occurrence, payment_method }) => ({
    title: expense.title,
    description: expense.description || "",
    vendor: venMap[expense.vendor_id]?.name || "",
    category: catMap[expense.category_id]?.name || "",
    due_date: occurrence?.due_date || expense.start_date,
    planned_amount: occurrence?.effective_amount ?? expense.planned_amount,
    paid: occurrence?.total_paid || 0,
    remaining: occurrence ? Number(occurrence.effective_amount) - Number(occurrence.total_paid || 0) : "",
    method: payment_method ? (paymentMethodIT[payment_method] || payment_method) : "",
    status: statusIT[occurrence?.status] || "—",
    notes: expense.notes || "",
  }));

  // PDF export config (iter43). Colonne stampa: data · fornitore · descrizione
  // · categoria · metodo · importo · stato. Totali finali dai KPI già
  // calcolati sopra.
  const pdfSubtitle = [
    `Periodo: ${fmtDate(dateFrom)} → ${fmtDate(dateTo)}`,
    vendorId ? `Fornitore: ${venMap[vendorId]?.name || "—"}` : null,
    categoryId ? `Categoria: ${catMap[categoryId]?.name || "—"}` : null,
    statusFilter !== "all" ? `Stato: ${statusIT[statusFilter] || statusFilter}` : null,
    query.trim() ? `Ricerca: "${query.trim()}"` : null,
  ].filter(Boolean).join(" · ");

  const pdfRows = exportRows.map((r) => ({
    date: r.due_date,
    vendor: r.vendor,
    description: r.title + (r.description ? ` — ${r.description}` : ""),
    category: r.category,
    method: r.method,
    amount: r.planned_amount,
    status: r.status,
  }));

  const pdfColumns = [
    { key: "date", header: "Data", type: "date", width: 22 },
    { key: "vendor", header: "Fornitore", type: "string", width: 32 },
    { key: "description", header: "Descrizione", type: "string" },
    { key: "category", header: "Categoria", type: "string", width: 30 },
    { key: "method", header: "Metodo", type: "string", width: 22 },
    { key: "amount", header: "Importo", type: "currency", width: 22, align: "right" },
    { key: "status", header: "Stato", type: "string", width: 22 },
  ];

  const pdfTotals = [
    { label: "Totale periodo", value: EUR(kpi.planned) },
    { label: "Pagato", value: EUR(kpi.paid) },
    { label: "Da pagare", value: EUR(kpi.remaining) },
    { label: "N° voci", value: String(kpi.count) },
  ];

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteExpense(confirmDelete.expense.id);
      toast.success("Spesa ordinaria eliminata.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Impossibile eliminare."));
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="ordinary-expenses-page">
      <PageHeader
        title="Spese ordinarie"
        subtitle="Spese operative pianificabili (bollette stimate, fatture occasionali, costi variabili). Generano scadenze ma non popolano l'archivio delle serie ricorrenti."
        actions={
          <Button asChild data-testid="btn-new-ordinary">
            <Link to="/ordinary/new">
              <Plus size={14} className="mr-1.5" />
              Nuova spesa ordinaria
            </Link>
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiTile label="Totale" value={EUR(kpi.planned)} testId="kpi-planned" />
        <KpiTile label="Pagato" value={EUR(kpi.paid)} testId="kpi-paid" tone="emerald" />
        <KpiTile label="Da pagare" value={EUR(kpi.remaining)} testId="kpi-remaining" tone={kpi.remaining > 0 ? "amber" : "stone"} />
        <KpiTile label="Voci" value={`${kpi.paidCount}/${kpi.count}`} testId="kpi-count" sublabel="pagate/totali" />
        <KpiTile label="Scadute" value={String(kpi.overdue)} testId="kpi-overdue" tone={kpi.overdue > 0 ? "rose" : "stone"} />
      </div>

      {/* Filters */}
      <div
        data-testid="ordinary-filters"
        className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-stone-200 bg-white p-3 md:grid-cols-7"
      >
        <div className="md:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Cerca
          </Label>
          <div className="relative">
            <Search
              size={14}
              strokeWidth={1.8}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <Input
              data-testid="filter-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Titolo, descrizione, fornitore…"
              className="pl-8 pr-8"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Pulisci"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Dal</Label>
          <Input
            type="date"
            data-testid="filter-date-from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Al</Label>
          <Input
            type="date"
            data-testid="filter-date-to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Fornitore</Label>
          <Select value={vendorId || "all"} onValueChange={(v) => setVendorId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-vendor"><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {vens.filter((v) => v.is_active).map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Categoria</Label>
          <Select value={categoryId || "all"} onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {cats.filter((c) => c.is_active).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">Stato</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Export bar */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-[11px] uppercase tracking-wider text-stone-500">
            {filtered.length} di {rows.length}
          </span>
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-stone-600">
            <input
              type="checkbox"
              data-testid="toggle-show-archived"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Mostra archiviati / interrotti
          </label>
        </div>
        <ExportButtons
          rows={exportRows}
          columns={[
            { key: "title", header: "Descrizione" },
            { key: "vendor", header: "Fornitore" },
            { key: "category", header: "Categoria" },
            { key: "due_date", header: "Scadenza", type: "date" },
            { key: "planned_amount", header: "Importo (€)", type: "currency" },
            { key: "paid", header: "Pagato (€)", type: "currency" },
            { key: "remaining", header: "Da pagare (€)", type: "currency" },
            { key: "method", header: "Metodo" },
            { key: "status", header: "Stato" },
            { key: "notes", header: "Note" },
          ]}
          filenameBase={buildFilename("spese_ordinarie", dateFrom, dateTo)}
          testId="export-ordinary"
          pdf={{
            title: "Spese ordinarie",
            subtitle: pdfSubtitle,
            rows: pdfRows,
            columns: pdfColumns,
            totals: pdfTotals,
            filename: buildFilename("spese_ordinarie", dateFrom, dateTo),
            orientation: "landscape",
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead
                column="due_date"
                sort={sort}
                onSort={toggleSort}
                testId="sort-due-date"
                className="w-[120px]"
              >
                Data scadenza
              </SortableHead>
              <SortableHead
                column="vendor"
                sort={sort}
                onSort={toggleSort}
                testId="sort-vendor"
                className="w-[180px]"
              >
                Fornitore
              </SortableHead>
              <SortableHead
                column="description"
                sort={sort}
                onSort={toggleSort}
                testId="sort-description"
              >
                Descrizione
              </SortableHead>
              <SortableHead
                column="amount"
                sort={sort}
                onSort={toggleSort}
                testId="sort-amount"
                className="w-[140px] text-right"
                align="right"
              >
                Importo
              </SortableHead>
              <SortableHead
                column="status"
                sort={sort}
                onSort={toggleSort}
                testId="sort-status"
                className="w-[120px]"
              >
                Stato
              </SortableHead>
              <TableHead className="w-[220px] text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-sm text-stone-500">
                  Caricamento…
                </TableCell>
              </TableRow>
            )}
            {!loading && sortedFiltered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center">
                  <p className="text-sm text-stone-500">Nessuna spesa ordinaria nel periodo.</p>
                  <Button asChild className="mt-4" data-testid="btn-empty-create">
                    <Link to="/ordinary/new">Crea la prima spesa ordinaria</Link>
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              paged.map(({ expense, occurrence }) => {
                const due = occurrence?.due_date || expense.start_date;
                const eff = Number(occurrence?.effective_amount ?? expense.planned_amount);
                const paid = Number(occurrence?.total_paid || 0);
                const remaining = eff - paid;
                const status = occurrence?.status || "planned";
                const todayIso = new Date().toISOString().slice(0, 10);
                const overdue =
                  (status === "planned" || status === "partially_paid") &&
                  due < todayIso;
                return (
                  <TableRow key={expense.id} data-testid={`ordinary-row-${expense.id}`}>
                    <TableCell>
                      <span className={"font-mono text-xs tabular-nums " + (overdue ? "text-rose-700 font-semibold" : "text-stone-700")}>
                        {fmtDate(due)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-stone-800">
                        {venMap[expense.vendor_id]?.name || "Senza fornitore"}
                      </div>
                      <div className="text-[11px] text-stone-500">
                        {catMap[expense.category_id]?.name || "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-semibold text-stone-900">
                        {occurrence?.effective_title || expense.title}
                      </div>
                      {expense.description && (
                        <div className="text-[11px] text-stone-500">{expense.description}</div>
                      )}
                      {expense.archived_at && (
                        <span
                          data-testid={`badge-archived-${expense.id}`}
                          className="mt-0.5 inline-block rounded bg-stone-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-stone-600 border border-stone-200"
                        >
                          Archiviata
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-mono text-sm font-semibold tabular-nums text-stone-900">
                        {EUR(eff)}
                      </div>
                      {paid > 0 && status !== "paid" && (
                        <div className="font-mono text-[10px] tabular-nums text-emerald-700">
                          Pagato {EUR(paid)} · Residuo {EUR(remaining)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                          (overdue
                            ? "bg-rose-600 text-white"
                            : status === "paid"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : status === "partially_paid"
                            ? "border border-amber-200 bg-amber-50 text-amber-800"
                            : status === "cancelled" || status === "skipped"
                            ? "border border-stone-200 bg-stone-50 text-stone-500"
                            : "border border-blue-200 bg-blue-50 text-blue-700")
                        }
                      >
                        {overdue ? "Scaduta" : statusIT[status] || status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          data-testid={`btn-edit-ordinary-${expense.id}`}
                        >
                          <Link to={`/ordinary/${expense.id}/edit`}>Modifica</Link>
                        </Button>
                        <Button
                          asChild
                          size="sm"
                          variant="outline"
                          data-testid={`btn-duplicate-ordinary-${expense.id}`}
                        >
                          <Link to={`/ordinary/new?from_expense=${expense.id}`}>
                            Duplica
                          </Link>
                        </Button>
                        {expense.archived_at ? (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`btn-unarchive-ordinary-${expense.id}`}
                            onClick={async () => {
                              try {
                                await unarchiveExpense(expense.id);
                                toast.success("Spesa ripristinata.");
                                load();
                              } catch (e) {
                                toast.error(formatApiError(e, "Errore"));
                              }
                            }}
                          >
                            Ripristina
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`btn-archive-ordinary-${expense.id}`}
                            onClick={async () => {
                              try {
                                await archiveExpense(expense.id);
                                toast.success("Spesa archiviata.");
                                load();
                              } catch (e) {
                                toast.error(formatApiError(e, "Errore"));
                              }
                            }}
                          >
                            Archivia
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`btn-delete-ordinary-${expense.id}`}
                          onClick={() => setConfirmDelete({ expense, occurrence })}
                        >
                          Elimina
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        {/* Pagination footer */}
        {!loading && sortedFiltered.length > 0 && (
          <div
            data-testid="ordinary-pagination"
            className="flex items-center justify-between border-t border-stone-200 bg-stone-50/40 px-3 py-2 text-xs text-stone-600"
          >
            <div className="flex items-center gap-2">
              <span>Righe per pagina</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger
                  data-testid="pagination-size"
                  className="h-7 w-20 text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-stone-400">
                · {(page - 1) * pageSize + 1}–
                {Math.min(page * pageSize, sortedFiltered.length)} di{" "}
                {sortedFiltered.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                data-testid="pagination-prev"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-7 px-2"
              >
                <ChevronLeft size={14} />
              </Button>
              <span className="px-2 font-mono tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                data-testid="pagination-next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-7 px-2"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-ordinary">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente la spesa ordinaria?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;eliminazione è permessa solo se la registrazione è ancora{" "}
              <strong>vergine</strong>: nessun pagamento registrato e nessuna
              scadenza cancellata. Altrimenti il sistema rifiuterà l&apos;operazione
              e dovrai usare <em>Archivia</em> o <em>Storna</em>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-ordinary"
              onClick={onDelete}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiTile({ label, value, sublabel, tone = "stone", testId }) {
  const colors = {
    stone: "text-stone-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-stone-200 bg-white p-3"
    >
      <div className="text-[10px] uppercase tracking-wider text-stone-500">
        {label}
      </div>
      <div className={"font-mono text-base font-semibold tabular-nums " + (colors[tone] || colors.stone)}>
        {value}
      </div>
      {sublabel && (
        <div className="text-[10px] text-stone-400">{sublabel}</div>
      )}
    </div>
  );
}

/**
 * Clickable, sortable table header.
 *
 * Shows an arrow icon next to the label:
 *   - inactive column: faded ArrowUpDown
 *   - active column:   ArrowUp / ArrowDown reflecting sort.direction
 */
function SortableHead({
  column, sort, onSort, testId, align = "left", className = "", children,
}) {
  const isActive = sort.key === column;
  const Icon = isActive
    ? sort.direction === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => onSort(column)}
        className={
          "inline-flex w-full items-center gap-1 select-none " +
          (align === "right" ? "justify-end" : "") +
          " " +
          (isActive ? "text-stone-900" : "text-stone-500 hover:text-stone-800")
        }
        aria-sort={isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <span>{children}</span>
        <Icon size={12} className={isActive ? "" : "opacity-40"} />
      </button>
    </TableHead>
  );
}
